import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { useServerFilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { toPickerPeriods } from '@app/components/multi-period-picker';
import { useGetPurchasedFoodAndDrinkList } from '@shared/api/hooks/purchased-goods/food-and-drink';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

/**
 * Shared toolbar for the Excluded items hub — search plus the Site / Time
 * period / Supplier / Import / Tags filters. Filter state lives in the URL
 * (nuqs), so every tab's table reads the same filters.
 *
 * NOTE (slice 1): filter options are sourced from the food list endpoint, which
 * already carries the cross-cutting facets. As each tab slice lands we can swap
 * to composed per-type options without changing this component's contract.
 */

const filterOptionKeys = [
  'sites',
  'timePeriods',
  'suppliers',
  'activitiesImport',
  'tags',
] as const;

export default function ExcludedItemsToolbar() {
  const { data } = useGetPurchasedFoodAndDrinkList();

  const filterOptions = useServerFilterOptions(
    filterOptionKeys,
    data?.filters || {}
  );

  const columns = useMemo<ColumnDef<Record<string, never>>[]>(() => {
    const sorting = { ascLabel: 'A-Z', descLabel: 'Z-A' };
    const facet = (
      id: string,
      label: string,
      options: { label: string; value: string }[]
    ): ColumnDef<Record<string, never>> => ({
      id,
      accessorFn: () => '',
      enableColumnFilter: true,
      meta: { label, variant: 'multiSelect', options, sorting },
    });

    return [
      {
        id: 'search',
        accessorFn: () => '',
        enableColumnFilter: true,
        meta: {
          label: 'Search',
          placeholder: 'Search items',
          variant: 'text',
          sorting,
        },
      },
      facet('site', 'Site', filterOptions.sitesFilter),
      {
        id: 'period',
        accessorFn: () => '',
        enableColumnFilter: true,
        meta: {
          label: 'Time period',
          variant: 'periodPicker',
          periods: toPickerPeriods(data?.filters?.timePeriods),
          sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
        },
      },
      facet('supplier', 'Supplier', filterOptions.suppliersFilter),
      facet('activitiesImport', 'Import', filterOptions.activitiesImportFilter),
      facet('tag', 'Tags', filterOptions.tagsFilter),
    ];
  }, [filterOptions, data?.filters?.timePeriods]);

  const { table } = useServerDataTable<Record<string, never>>({
    data: [],
    columns,
    pageCount: 0,
    shallow: false,
  });

  return <DataTableToolbar table={table} />;
}
